import React from 'react';

import type { EventAlertDialogProps } from '@looko-design-system';
import { LookoButton, LookoModal } from '@looko-design-system';
import { Button, Typography } from '@mui/material';

import { useBoolean, useDebounce } from '@looko-corp/acloset-react-kit/src/react';

/**
 * AlertDialog를 바탕으로 생성한 Modal Dialog입니다.
 * @see https://www.figma.com/design/PFrAvDaThtf8qMSRc9pNaV/LDS(Looko-Design-System)?node-id=4323-8097&t=oPAUvWkM0LriUQzv-11
 */
export const EventModalDialog = (props: EventAlertDialogProps) => {
  const { value: isHandling, setValue: setHandling } = useBoolean(false);

  const handleCallback = useDebounce(async () => {
    setHandling(true);
    try {
      if (props.onConfirm ? await props.onConfirm() : true) {
        props.onClose?.();
      }
    } finally {
      setHandling(false);
    }
  });

  const onReject = useDebounce(async () => {
    setHandling(true);
    try {
      if (props.onReject ? await props.onReject() : true) {
        props.onClose?.();
      }
    } finally {
      setHandling(false);
    }
  });

  React.useLayoutEffect(() => {
    if (!props.open) {
      setHandling(false);
    }
  }, [props.open, setHandling]);

  return (
    <LookoModal open={props.open} onClose={props.disableEscape ? undefined : props.onClose}>
      {props.title != null && (
        <LookoModal.Header>
          <Typography variant="H2_Looko" style={{ whiteSpace: 'pre-line' }} color={props.titleColor}>
            {props.title}
          </Typography>
        </LookoModal.Header>
      )}
      {props.content != null && (
        <LookoModal.Content>
          {typeof props.content === 'string' ? (
            <Typography variant="Body_Looko" whiteSpace={'pre-line'}>
              {props.content}
            </Typography>
          ) : (
            props.content
          )}
        </LookoModal.Content>
      )}

      <LookoModal.Actions>
        {props.confirmButtonText != null && (
          <LookoButton
            loading={isHandling}
            onClick={handleCallback}
            autoFocus
            variant="contained"
            color={props.confirmButtonColor}
          >
            {props.confirmButtonText}
          </LookoButton>
        )}
        {props.rejectButtonText != null && (
          <LookoButton variant="outlined" onClick={onReject} autoFocus color={props.rejectButtonColor}>
            {props.rejectButtonText}
          </LookoButton>
        )}

        {props.cancelButtonText != null && (
          <LookoButton variant="outlined" onClick={props.onClose}>
            {props.cancelButtonText}
          </LookoButton>
        )}
      </LookoModal.Actions>
    </LookoModal>
  );
};
